"use client";

import { cn } from "@/lib/utils";
import { useEffect, useMemo, useRef, useState } from "react";
// [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → central widget_impression; see webapp/docs/analytics
import { tabSwitched } from "@/customAnalytics/trackingEvents";
import { Faq3CategoryRail } from "./Faq3CategoryRail";
import { Faq3NumberedAccordion } from "./Faq3NumberedAccordion";
import { buildFaq3ViewModel } from "./viewModel";
import { useFaq3Selection } from "./useFaq3Selection";

type Props = { data: { data?: Record<string, unknown> } & Record<string, unknown> };

/**
 * FAQ variant 3 — layout is fixed; all CMS fields are normalized in `buildFaq3ViewModel`.
 */
export function Faq3Section({ data }: Props) {
  const ref = useRef<HTMLElement>(null);
  const vm = useMemo(() => buildFaq3ViewModel(data), [data]);

  const { categoryId, selectCategory, selected, items } = useFaq3Selection(vm.categories);
  const [expandedFaqId, setExpandedFaqId] = useState<string | null>(null);

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current) FireWidgetViewedEvent(ref, "faqs_section");
  // }, []);

  useEffect(() => {
    const cat = categoryId != null ? vm.categories.find((c) => c.id === categoryId) : null;
    const first = cat?.items[0];
    setExpandedFaqId(first ? first.id.toString() : null);
  }, [categoryId, vm.categories]);

  const onSelectCategory = (id: number) => {
    const cat = vm.categories.find((c) => c.id === id);
    if (cat) {
      tabSwitched({
        section: "faqs_section",
        component: "category_tab",
        label: cat.name,
      });
    }
    selectCategory(id);
  };

  const { section: backdrop } = vm;
  const hasBgImage = Boolean(backdrop.imageUrl);

  return (
    <section
      ref={ref}
      className={cn(
        "[&_*]:![font-family:Poppins,sans-serif]",
        "relative w-full overflow-hidden",
      )}
      style={{
        backgroundColor: backdrop.fallbackBg,
        fontFamily: "Poppins, sans-serif",
      }}
    >
      {hasBgImage ? (
        <>
          <div
            className={cn(
              "pointer-events-none absolute inset-0 z-0 bg-cover bg-center bg-no-repeat",
              /* Section height changes per category (different FAQ counts); fixed attachment
                 keeps the same crop relative to the viewport instead of re-scaling cover. */
              "bg-fixed motion-reduce:bg-scroll",
            )}
            style={{ backgroundImage: `url(${backdrop.imageUrl})` }}
            aria-hidden
          />
          <div
            className="pointer-events-none absolute inset-0 z-[1] bg-white/90"
            aria-hidden
          />
        </>
      ) : null}

      <div
        className={cn(
          "relative z-[2] mx-auto flex w-full max-w-[1100px] flex-col items-center",
          "gap-6 px-5 py-8 min-[640px]:px-6 min-[640px]:py-10 min-[768px]:gap-8 min-[768px]:py-12 min-[1024px]:px-8",
        )}
      >
        <div className={cn("flex w-full max-w-3xl flex-col items-center gap-1.5 text-center")}>
          <h2
            className="text-[28px] font-semibold tracking-[-0.8px] min-[640px]:text-[32px]"
            style={{ color: vm.header.titleColor }}
          >
            {vm.header.title}
          </h2>
          {vm.header.subtitle ? (
            <p
              className="w-full text-base font-normal min-[640px]:text-lg"
              style={{ color: vm.header.subtitleColor }}
            >
              {vm.header.subtitle}
            </p>
          ) : null}
        </div>

        <div
          className={cn(
            "flex w-full flex-1 flex-col gap-5 min-[1024px]:flex-row min-[1024px]:items-start min-[1024px]:gap-8",
          )}
        >
          <Faq3CategoryRail
            brandColor={vm.theme.brandColor}
            categories={vm.categories}
            selectedId={categoryId}
            onSelect={onSelectCategory}
          />

          <div className="min-w-0 flex-1">
            <Faq3NumberedAccordion
              theme={vm.theme}
              items={items}
              categoryName={selected?.name ?? ""}
              expandedFaqId={expandedFaqId}
              onExpandedChange={setExpandedFaqId}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
